/**
 * MyBriefsView — personal library of AI-drafted MVP briefs.
 * Lists every brief saved from G2 Pain Index, click to expand inline,
 * download CSV/PDF, or delete.
 */
import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MyBriefsView = () => {
  const [briefs, setBriefs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/briefs`, { timeout: 10000 })
      .then((r) => setBriefs(r.data?.briefs || []))
      .catch(() => setBriefs([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    if (!window.confirm("Delete this brief? This can't be undone.")) return;
    try {
      await axios.delete(`${API}/briefs/${id}`, { timeout: 8000 });
      setBriefs((prev) => prev.filter((b) => b.id !== id));
    } catch (e) {
      alert(e?.response?.data?.detail || "Delete failed");
    }
  };

  if (loading) {
    return (
      <div className="px-1 py-8 text-xs text-zinc-400 uppercase tracking-[0.2em] animate-pulse">
        Loading your briefs…
      </div>
    );
  }

  if (briefs.length === 0) {
    return (
      <div className="bg-white border border-zinc-200 rounded-2xl p-10 text-center" data-testid="my-briefs-empty">
        <div className="text-4xl mb-3">📓</div>
        <h3 className="text-base font-semibold text-zinc-900 mb-1">No briefs saved yet</h3>
        <p className="text-sm text-zinc-500 max-w-md mx-auto">
          Head to the Dashboard → G2 Pain Index → click <span className="font-mono-data text-violet-600">⚡ BUILD ALT</span> on any product → save the brief to populate your library.
        </p>
      </div>
    );
  }

  return (
    <section className="space-y-3" data-testid="my-briefs-view">
      <header className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-lg font-semibold text-zinc-900">My MVP Briefs</h2>
          <p className="text-xs text-zinc-500 mt-0.5">{briefs.length} brief{briefs.length === 1 ? "" : "s"} — your personal launch portfolio.</p>
        </div>
        <button
          onClick={load}
          className="text-[10px] font-mono-data uppercase tracking-[0.15em] text-zinc-600 hover:text-zinc-900"
          data-testid="my-briefs-refresh"
        >
          ↻ Refresh
        </button>
      </header>

      <ul className="space-y-3" data-testid="my-briefs-list">
        {briefs.map((row) => {
          const b = row.brief || {};
          const isOpen = openId === row.id;
          return (
            <li
              key={row.id}
              className="bg-white border border-zinc-200 rounded-2xl overflow-hidden"
              data-testid={`my-briefs-row-${row.id}`}
            >
              <div className="px-5 py-4 flex flex-wrap items-center gap-3 justify-between">
                <button
                  onClick={() => setOpenId(isOpen ? null : row.id)}
                  className="flex-1 min-w-0 text-left"
                  data-testid={`my-briefs-toggle-${row.id}`}
                >
                  <div className="text-[10px] font-mono-data uppercase tracking-[0.2em] text-violet-600">
                    Alternative to · {row.product_name}
                  </div>
                  <div className="text-sm font-medium text-zinc-900 mt-0.5 truncate">
                    {b.positioning || "Untitled brief"}
                  </div>
                  <div className="text-[11px] text-zinc-500 mt-0.5">
                    Saved {new Date(row.updated_at || row.created_at || Date.now()).toLocaleDateString()}
                    {row.complaints_count ? ` · ${row.complaints_count} complaints analyzed` : ""}
                  </div>
                </button>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <a
                    href={`${API}/briefs/${row.id}/export.csv`}
                    download
                    className="px-3 py-1.5 text-[11px] font-mono-data uppercase tracking-[0.15em] rounded-full bg-white border border-zinc-200 text-zinc-700 hover:bg-zinc-50"
                    data-testid={`my-briefs-csv-${row.id}`}
                  >
                    ⬇ CSV
                  </a>
                  <a
                    href={`${API}/briefs/${row.id}/export.pdf`}
                    download
                    className="px-3 py-1.5 text-[11px] font-mono-data uppercase tracking-[0.15em] rounded-full bg-white border border-zinc-200 text-zinc-700 hover:bg-zinc-50"
                    data-testid={`my-briefs-pdf-${row.id}`}
                  >
                    ⬇ PDF
                  </a>
                  <button
                    onClick={() => remove(row.id)}
                    title="Delete brief"
                    className="px-2.5 py-1.5 text-[11px] font-mono-data uppercase tracking-[0.15em] rounded-full bg-white border border-zinc-200 text-rose-600 hover:bg-rose-50"
                    data-testid={`my-briefs-delete-${row.id}`}
                  >
                    ✕
                  </button>
                  <span className="text-zinc-400 text-xs">{isOpen ? "▾" : "▸"}</span>
                </div>
              </div>

              {isOpen && (
                <div className="px-5 pb-5 pt-1 space-y-3 text-sm border-t border-zinc-100" data-testid={`my-briefs-body-${row.id}`}>
                  <div>
                    <div className="text-[10px] font-mono-data uppercase tracking-[0.2em] text-zinc-500 mb-1">Target persona</div>
                    <div className="text-zinc-800">{b.target_persona || "—"}</div>
                  </div>
                  <div>
                    <div className="text-[10px] font-mono-data uppercase tracking-[0.2em] text-zinc-500 mb-2">Top 3 features</div>
                    <ol className="space-y-2">
                      {(b.top_3_features || []).map((f, i) => (
                        <li key={i} className="bg-violet-50 border border-violet-100 rounded-lg p-3">
                          <div className="flex items-start gap-2">
                            <span className="font-mono-data text-[10px] text-violet-600 mt-0.5">{i + 1}</span>
                            <span className="text-zinc-900">{f}</span>
                          </div>
                          {b.feature_evidence?.[i] && (
                            <blockquote className="mt-2 text-[11px] text-zinc-600 border-l-2 border-violet-300 pl-2 italic">
                              evidence: "{b.feature_evidence[i]}"
                            </blockquote>
                          )}
                        </li>
                      ))}
                    </ol>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <div className="text-[10px] font-mono-data uppercase tracking-[0.2em] text-zinc-500 mb-1">Pricing</div>
                      <div className="text-zinc-900 font-semibold">{b.suggested_pricing || "—"}</div>
                    </div>
                    <div>
                      <div className="text-[10px] font-mono-data uppercase tracking-[0.2em] text-zinc-500 mb-1">GTM angle</div>
                      <div className="text-zinc-800 italic">"{b.gtm_angle || "—"}"</div>
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] font-mono-data uppercase tracking-[0.2em] text-zinc-500 mb-1">Ad headline</div>
                    <div className="text-zinc-900 text-base font-semibold bg-amber-50 border border-amber-200 rounded-lg p-3">
                      "{b.first_ad_headline || "—"}"
                    </div>
                  </div>
                  {row.g2_url && (
                    <a
                      href={row.g2_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-block text-[10px] font-mono-data uppercase tracking-[0.15em] text-violet-700 hover:underline pt-1"
                    >
                      Read the raw complaints on G2 ↗
                    </a>
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
};

export default MyBriefsView;
